// Выгрузка сводки по дашборду (Показатели + Дисциплина) в Excel для анализа.
import * as XLSX from 'xlsx';
import { aggregateWeeks } from './data';

// Периоды: Месяц + каждая неделя → [{ key, weeks }]
function periodsOf(weekNums) {
  return [{ key: 'Месяц', weeks: weekNums }, ...weekNums.map(w => ({ key: 'W' + w, weeks: [w] }))];
}

// Одна строка метрик по набору маршрутов за период (из свёртки rweeks)
function metricRow(cols, routes, weekNums, rweeks) {
  const a = aggregateWeeks(routes, weekNums, rweeks);
  const active = routes.filter(r => !r.is_vacancy).length;
  return {
    ...cols,
    'Выходов': a.exits,
    'Маршрутов всего': a.routes,
    'Активных маршрутов': active,
    'Заказ (визиты)': a.order,
    'Прогноз (визиты)': a.plan,
    'Факт BI (визиты)': a.fact,
    'Прогноз/Заказ %': a.order ? Math.round(a.plan / a.order * 100) : '',
    'Прогноз/Факт %': a.fact ? Math.round(a.plan / a.fact * 100) : '',
    'SL Факт/Заказ %': a.order ? Math.round(a.fact / a.order * 100) : '',
    'Перекрытия': a.overlaps,
    'БЛ непокрытые': a.sick,
    'ОТ непокрытые': a.vac,
    'Вакансии (маршр)': a.vacRoutes,
    'Вакансии (дни)': a.vacDays,
  };
}

function autoWidths(rows) {
  if (!rows.length) return [];
  return Object.keys(rows[0]).map(k => ({
    wch: Math.min(40, Math.max(k.length, ...rows.map(r => String(r[k] ?? '').length)) + 2),
  }));
}

function addSheet(wb, name, rows) {
  const ws = XLSX.utils.json_to_sheet(rows);
  ws['!cols'] = autoWidths(rows);
  XLSX.utils.book_append_sheet(wb, ws, name.slice(0, 31));
}

function stamp() {
  const d = new Date();
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}_${p(d.getHours())}${p(d.getMinutes())}`;
}

// РМ: весь проект → BU → ТМ → СВ → маршруты
export function downloadRmSummary(tmData, weeks, weekNums, rweeks) {
  const periods = periodsOf(weekNums);
  const buOf = g => g.sv.bu || 'Без BU';

  const proj = [], byBu = [], byTm = [], bySv = [], byRoute = [];
  const allRoutes = tmData.flatMap(t => t.svGroups.flatMap(g => g.routes));

  periods.forEach(p => proj.push(metricRow({ 'Период': p.key }, allRoutes, p.weeks, rweeks)));

  const buSet = Array.from(new Set(tmData.flatMap(t => t.svGroups.map(buOf)))).sort();
  buSet.forEach(bu => {
    const routes = tmData.flatMap(t => t.svGroups.filter(g => buOf(g) === bu).flatMap(g => g.routes));
    periods.forEach(p => byBu.push(metricRow({ 'BU': bu, 'Период': p.key }, routes, p.weeks, rweeks)));
  });

  tmData.forEach(({ tm, svGroups }) => {
    const bu = svGroups.length ? buOf(svGroups[0]) : '';
    const routes = svGroups.flatMap(g => g.routes);
    periods.forEach(p => byTm.push(metricRow({ 'BU': bu, 'ТМ': tm.full_name, 'Период': p.key }, routes, p.weeks, rweeks)));
    svGroups.forEach(({ sv, routes: svRoutes }) => {
      periods.forEach(p => bySv.push(metricRow({ 'BU': buOf({ sv }), 'ТМ': tm.full_name, 'СВ': sv.full_name, 'Период': p.key }, svRoutes, p.weeks, rweeks)));
      // Маршруты — только по месяцу, чтобы файл не раздувать
      svRoutes.forEach(r => byRoute.push(metricRow(
        { 'BU': buOf({ sv }), 'ТМ': tm.full_name, 'СВ': sv.full_name, 'Внутр. код': r.internal_code || '', 'Внешний код': r.external_code || '', 'Вакансия': r.is_vacancy ? 'да' : '', 'Период': 'Месяц' },
        [r], periods[0].weeks, rweeks)));
    });
  });

  const wb = XLSX.utils.book_new();
  addSheet(wb, 'Проект', proj);
  addSheet(wb, 'По BU', byBu);
  addSheet(wb, 'По ТМ', byTm);
  addSheet(wb, 'По СВ', bySv);
  addSheet(wb, 'Маршруты (месяц)', byRoute);
  XLSX.writeFile(wb, `Сводка_табель_проект_${stamp()}.xlsx`);
}

// ТМ: группа → СВ → маршруты
export function downloadTmSummary(svGroups, tmName, weeks, weekNums, rweeks) {
  const periods = periodsOf(weekNums);
  const group = [], bySv = [], byRoute = [];
  const allRoutes = svGroups.flatMap(g => g.routes);

  periods.forEach(p => group.push(metricRow({ 'Период': p.key }, allRoutes, p.weeks, rweeks)));
  svGroups.forEach(({ sv, routes }) => {
    periods.forEach(p => bySv.push(metricRow({ 'СВ': sv.full_name, 'Период': p.key }, routes, p.weeks, rweeks)));
    routes.forEach(r => byRoute.push(metricRow(
      { 'СВ': sv.full_name, 'Внутр. код': r.internal_code || '', 'Внешний код': r.external_code || '', 'Вакансия': r.is_vacancy ? 'да' : '', 'Период': 'Месяц' },
      [r], periods[0].weeks, rweeks)));
  });

  const wb = XLSX.utils.book_new();
  addSheet(wb, 'Группа', group);
  addSheet(wb, 'По СВ', bySv);
  addSheet(wb, 'Маршруты (месяц)', byRoute);
  const safe = (tmName || 'ТМ').replace(/[^\wа-яА-ЯёЁ-]+/g, '_');
  XLSX.writeFile(wb, `Сводка_табель_${safe}_${stamp()}.xlsx`);
}

// ── ВЫГРУЗКА ПРОГНОЗА (Прогноз + Заказ + %, недели колонками + Итог) ──────────
// Одна строка: идентификаторы + по каждой неделе Прогноз/Заказ + итог за месяц и % Прогноз/Заказ.
function forecastRow(cols, routes, weekNums, rweeks) {
  const row = { ...cols };
  let tPlan = 0, tOrd = 0;
  weekNums.forEach(w => {
    const a = aggregateWeeks(routes, [w], rweeks);
    row[`Прогноз W${w}`] = a.plan;
    row[`Заказ W${w}`]   = a.order;
    tPlan += a.plan; tOrd += a.order;
  });
  row['Прогноз Итог'] = tPlan;
  row['Заказ Итог']   = tOrd;
  row['Прогноз/Заказ %'] = tOrd ? Math.round(tPlan / tOrd * 100) : '';
  return row;
}

// РМ (jesus): прогноз по BU / ТМ / СВ
export function downloadForecastRm(tmData, weekNums, rweeks) {
  const buOf = g => g.sv.bu || 'Без BU';
  const allRoutes = tmData.flatMap(t => t.svGroups.flatMap(g => g.routes));

  const byBu = [], byTm = [], bySv = [];
  const buSet = Array.from(new Set(tmData.flatMap(t => t.svGroups.map(buOf)))).sort();
  buSet.forEach(bu => {
    const routes = tmData.flatMap(t => t.svGroups.filter(g => buOf(g) === bu).flatMap(g => g.routes));
    byBu.push(forecastRow({ 'BU': bu }, routes, weekNums, rweeks));
  });
  byBu.push(forecastRow({ 'BU': 'ИТОГО проект' }, allRoutes, weekNums, rweeks));

  tmData.forEach(({ tm, svGroups }) => {
    const bu = svGroups.length ? buOf(svGroups[0]) : '';
    byTm.push(forecastRow({ 'BU': bu, 'ТМ': tm.full_name }, svGroups.flatMap(g => g.routes), weekNums, rweeks));
    svGroups.forEach(({ sv, routes }) => bySv.push(forecastRow({ 'BU': buOf({ sv }), 'ТМ': tm.full_name, 'СВ': sv.full_name }, routes, weekNums, rweeks)));
  });

  const wb = XLSX.utils.book_new();
  addSheet(wb, 'Прогноз по BU', byBu);
  addSheet(wb, 'Прогноз по ТМ', byTm);
  addSheet(wb, 'Прогноз по СВ', bySv);
  XLSX.writeFile(wb, `Прогноз_проект_${stamp()}.xlsx`);
}

// ТМ: прогноз по СВ своей группы
export function downloadForecastTm(svGroups, tmName, weekNums, rweeks) {
  const all = svGroups.flatMap(g => g.routes);
  const bySv = [ forecastRow({ 'СВ': 'ИТОГО группа' }, all, weekNums, rweeks) ];
  svGroups.forEach(({ sv, routes }) => bySv.push(forecastRow({ 'СВ': sv.full_name }, routes, weekNums, rweeks)));

  const wb = XLSX.utils.book_new();
  addSheet(wb, 'Прогноз по СВ', bySv);
  const safe = (tmName || 'ТМ').replace(/[^\wа-яА-ЯёЁ-]+/g, '_');
  XLSX.writeFile(wb, `Прогноз_${safe}_${stamp()}.xlsx`);
}
